import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  Checkbox,
  Grid,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactPlayer from 'react-player';
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { auth } from '../firebase/firebase';
import { db } from '../firebase/firebase';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay10Icon from '@mui/icons-material/Replay10';

const CourseContentPage = () => {
  const { courseName } = useParams();
  const [course, setCourse] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [completedVideos, setCompletedVideos] = useState({});
  const [playingVideoUrl, setPlayingVideoUrl] = useState("");
  const [currentVideoKey, setCurrentVideoKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playerRef = useRef(null);
  const user = auth.currentUser;
  const [playingAudioUrl, setPlayingAudioUrl] = useState("");
  const [currentAudioKey, setCurrentAudioKey] = useState(null);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const coursesCollection = collection(db, 'courses');
        const courseSnapshot = await getDocs(coursesCollection);
        const courseList = courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const selectedCourse = courseList.find(course => course.name.toLowerCase().replace(/\s+/g, '-') === courseName);
        setCourse(selectedCourse);

        if (selectedCourse && selectedCourse.curriculum.length > 0) {
          // Load the first video URL if available
          const firstVideo = selectedCourse.curriculum.flatMap(topic => topic.subParts)
            .flatMap(part => part.content)
            .find(content => content.type === 'video' || content.type === 'firebaseVideo');
          
          if (firstVideo) {
            setPlayingVideoUrl(firstVideo.url);
            setCurrentVideoKey(`${0}-${0}-${0}`); // Set a default key for the first video
          } else {
            setErrorMessage("No video available for this course."); // Set error message if no video is found
          }
        } else {
          setErrorMessage("No curriculum available for this course."); // Set error message if no curriculum is found
        }

        if (user) {
          const progressDocRef = doc(db, "userProgress", user.uid, "courses", courseName);
          const progressSnapshot = await getDoc(progressDocRef);
          if (progressSnapshot.exists()) {
            setCompletedVideos(progressSnapshot.data().completedVideos || {});
          }
        }
      } catch (error) {
        setErrorMessage("Error occurred while fetching course data."); // Handle any errors during fetch
      }
    };
    fetchCourse();
  }, [courseName, user]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handlePlayContent = (url, key, type) => {
    if (type === 'audio') {
      setPlayingAudioUrl(url);
      setCurrentAudioKey(key);
      setPlayingVideoUrl("");
    } else {
      setPlayingVideoUrl(url);
      setCurrentVideoKey(key);
      setPlayingAudioUrl("");
    }
    setIsPlaying(true);
  };

  const handleVideoCompletion = async (key) => {
    const updatedCompletedVideos = { ...completedVideos, [key]: true };
    setCompletedVideos(updatedCompletedVideos);

    if (user) {
      const progressDocRef = doc(db, "userProgress", user.uid, "courses", courseName);
      await setDoc(progressDocRef, { completedVideos: updatedCompletedVideos }, { merge: true });
    }
  };

  const handleVideoEnd = () => {
    if (currentVideoKey) {
      handleVideoCompletion(currentVideoKey);
    }
  };

  const handleAudioEnd = () => {
    if (currentAudioKey) {
      handleVideoCompletion(currentAudioKey);
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const seekForward = () => {
    if (playerRef.current) {
      playerRef.current.seekTo(playerRef.current.getCurrentTime() + 10);
    }
  };

  const seekBackward = () => {
    if (playerRef.current) {
      playerRef.current.seekTo(playerRef.current.getCurrentTime() - 10);
    }
  };

  const handlePlaybackSpeedChange = (event) => {
    setPlaybackSpeed(event.target.value);
  };

  if (!course) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress />
        <Typography variant="h5" sx={{ mt: 3, textAlign: 'center' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '2450px', mt: 4, px: { xs: 2, md: 4 }, overflow: 'hidden' }}>
      <Card sx={{ mt: 2, bgcolor: 'beige' }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{course.name}</Typography>
          <Typography variant="h6">Instructor: {course.author}</Typography>
        </CardContent>
      </Card>

      {errorMessage && (
        <Box sx={{ mt: 2, color: 'red' }}>
          <Typography variant="h6">{errorMessage}</Typography>
        </Box>
      )}

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={7}>
          <Box sx={{ mt: 4 }}>
            {playingVideoUrl && (
              <ReactPlayer
                ref={playerRef}
                url={playingVideoUrl}
                controls
                playing={isPlaying}
                width="100%"
                height="auto"
                style={{ aspectRatio: '16/9' }}
                onEnded={handleVideoEnd}
                playbackRate={playbackSpeed}
              />
            )}
            
            {playingAudioUrl && (
              <Box sx={{ width: '100%', p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <audio 
                  src={playingAudioUrl} 
                  controls 
                  style={{ width: '100%' }}
                  onEnded={handleAudioEnd}
                />
              </Box>
            )}

            {!playingVideoUrl && !playingAudioUrl && (
              <Typography variant="h6" sx={{ textAlign: 'center' }}>
                No media selected to play.
              </Typography>
            )}

            {playingVideoUrl.includes('firebase') && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <IconButton onClick={togglePlayPause}>
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <IconButton onClick={seekBackward}>
                  <Replay10Icon />
                </IconButton>
                <IconButton onClick={seekForward}>
                  <Forward10Icon />
                </IconButton>
                <Select
                  value={playbackSpeed}
                  onChange={handlePlaybackSpeedChange}
                  sx={{ ml: 2 }}
                >
                  <MenuItem value={0.5}>0.5x</MenuItem>
                  <MenuItem value={1}>1x</MenuItem>
                  <MenuItem value={1.5}>1.5x</MenuItem>
                  <MenuItem value={2}>2x</MenuItem>
                </Select>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>Curriculum</Typography>
          {course.curriculum && course.curriculum.map((topic, mainIndex) => (
            <Accordion key={mainIndex} expanded={expanded === mainIndex} onChange={handleAccordionChange(mainIndex)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{topic.mainTopic}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {topic.subParts.map((part, subIndex) => (
                  <Box key={subIndex} sx={{ mb: 2 }}>
                    <Typography variant="body1">{part.title}</Typography>
                    {part.content.map((content, contentIndex) => {
                      const key = `${mainIndex}-${subIndex}-${contentIndex}`;
                      return (
                        <Box key={contentIndex} sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                            {content.type === 'video' ? 'Video: ' : 
                             content.type === 'firebaseVideo' ? 'Firebase Video: ' : 
                             content.type === 'audio' ? 'Audio: ' : 'PDF: '}
                            {content.url}
                          </Typography>
                          <Checkbox
                            checked={completedVideos[key] || false}
                            onChange={() => handleVideoCompletion(key)}
                            sx={{ ml: 1 }}
                          />
                          {(content.type === 'video' || content.type === 'firebaseVideo') && (
                            <Button
                              variant="outlined"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() => handlePlayContent(content.url, key, 'video')}
                            >
                              Watch Video
                            </Button>
                          )}
                          {content.type === 'audio' && (
                            <Button
                              variant="outlined"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() => handlePlayContent(content.url, key, 'audio')}
                            >
                              Play Audio
                            </Button>
                          )}
                          {content.type === 'pdf' && (
                            <Button
                              variant="outlined"
                              color="primary"
                              sx={{ ml: 1 }}
                              onClick={() => window.open(content.url, '_blank')}
                            >
                              Open PDF
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CourseContentPage;